import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { BasicColumnTypes } from '@opengov/ogql-types';
import { FiscalYearOption, RelativeFilter, RelativeFilterOptions } from '../accountSummaryRelativeFilters';
import { endOfMonthInTimezone, todayInTimezone } from './todayInTimezone';

dayjs.extend(utc);

export const rollingNFilterOptions: RelativeFilter[] = [
  RelativeFilterOptions.RollingNFiscalYears,
  RelativeFilterOptions.RollingNCalenderYears,
  RelativeFilterOptions.RollingNFiscalQuarters,
  RelativeFilterOptions.RollingNCalendarQuarters,
  RelativeFilterOptions.RollingNMonths,
  RelativeFilterOptions.RollingNPeriods
];

//The range of the relative filters are fixed
export const fixedRelativeFilterOptions: RelativeFilter[] = [
  RelativeFilterOptions.InceptionToYear,
  RelativeFilterOptions.InceptionToDate,
  RelativeFilterOptions.InceptionToPeriod,

  //Fiscal Years
  RelativeFilterOptions.CurrentFiscalYear,
  RelativeFilterOptions.CurrentAndPriorFiscalYear,
  RelativeFilterOptions.PriorFiscalYear,
  RelativeFilterOptions.NextFiscalYear,
  RelativeFilterOptions.PriorTwoFiscalYears,
  RelativeFilterOptions.NextTwoFiscalYear,
  RelativeFilterOptions.CurrentAndPriorTwoFiscalYears,
  RelativeFilterOptions.CurrentFiscalYearToDate,
  RelativeFilterOptions.CurrentFiscalYearToCurrentMonth,
  RelativeFilterOptions.CurrentFiscalYearToPriorMonth,
  RelativeFilterOptions.CurrentFiscalYearToCurrentPeriod,
  RelativeFilterOptions.CurrentFiscalYearToPriorPeriod,
  RelativeFilterOptions.PriorFiscalYearToDate,
  RelativeFilterOptions.PriorFiscalYearToCurrentPeriod,

  //Calendar Years
  RelativeFilterOptions.CurrentCalendarYear,
  RelativeFilterOptions.PriorCalendarYear,
  RelativeFilterOptions.NextCalendarYear,
  RelativeFilterOptions.CurrentCalendarYearToDate,
  RelativeFilterOptions.CurrentCalendarYearToCurrentPeriod,
  RelativeFilterOptions.PriorCalendarYearToDate,
  RelativeFilterOptions.PriorCalendarYearToCurrentPeriod,

  //Fiscal Quarters
  RelativeFilterOptions.CurrentFiscalQuarter,
  RelativeFilterOptions.CurrentFiscalQuarterToDate,
  RelativeFilterOptions.CurrentFiscalQuarterToCurrentPeriod,
  RelativeFilterOptions.PriorFiscalQuarter,
  RelativeFilterOptions.PriorFiscalQuarterToDate,
  RelativeFilterOptions.PriorFiscalQuarterToCurrentPeriod,
  RelativeFilterOptions.NextFiscalQuarter,

  //Calendar Quarters
  RelativeFilterOptions.CurrentCalendarQuarter,
  RelativeFilterOptions.CurrentCalendarQuarterToDate,
  RelativeFilterOptions.CurrentCalendarQuarterToCurrentPeriod,
  RelativeFilterOptions.PriorCalendarQuarter,
  RelativeFilterOptions.PriorCalendarQuarterToDate,
  RelativeFilterOptions.PriorCalendarQuarterToCurrentPeriod,
  RelativeFilterOptions.NextCalendarQuarter,

  //Months
  RelativeFilterOptions.CalendarCalendarMonth,
  RelativeFilterOptions.CurrentMonth,
  RelativeFilterOptions.CurrentMonthToDate,
  RelativeFilterOptions.InceptionToCurrentMonth,
  RelativeFilterOptions.PriorMonth,
  RelativeFilterOptions.PriorMonthToDate,
  RelativeFilterOptions.NextMonth,

  //Periods
  RelativeFilterOptions.CurrentPeriod,
  RelativeFilterOptions.PriorPeriod,
  RelativeFilterOptions.PriorYearCurrentPeriod,
  RelativeFilterOptions.PriorYearPriorPeriod,
];

export const isFiscalYearYYYYOption = (selectedRelativeDate: RelativeFilter): boolean => {
  const options = [
    'CurrentFiscalYear',
    'PriorFiscalYear',
    'NextFiscalYear',
    'CurrentAndPriorFiscalYear',
    'NextTwoFiscalYear',
    'PriorTwoFiscalYears',
    'CurrentAndPriorTwoFiscalYears',
    'RollingNFiscalYears'
  ];
  return options.includes(selectedRelativeDate as FiscalYearOption);
};

//Special cases to handle when column type is year to match front-end display
//...see dateRangeCalculations
export const specialCasesForYears: RelativeFilter[] = [
  RelativeFilterOptions.CurrentFiscalYear,
  RelativeFilterOptions.NextFiscalYear,
  RelativeFilterOptions.PriorFiscalYear
];

export function isRollingNRelativeFilter(relativeFilterType: RelativeFilter) {
  return rollingNFilterOptions.includes(relativeFilterType);
}

export function isFixedRelativeFilter(relativeFilterType: RelativeFilter) {
  return fixedRelativeFilterOptions.includes(relativeFilterType);
}

export function isSpecialCaseForYearsRelativeFilter(relativeFilterType: RelativeFilter) {
  return specialCasesForYears.includes(relativeFilterType);
}

function formatDateMMDDYYYY(date: Date): string {
  const dayJsDate = dayjs(date);
  if (!dayJsDate.isValid()) {
    throw new Error(`Invalid date: ${date}`);
  }
  return dayJsDate.utc().format('MM/DD/YYYY');
}

function formatDateYYYY(date: Date): string {
  const dayJsDate = dayjs(date);
  if (!dayJsDate.isValid()) {
    throw new Error(`Invalid date: ${date}`);
  }
  return dayJsDate.utc().format('YYYY');
}

function formatDate(date: Date, type: BasicColumnTypes): string {
  if (type === BasicColumnTypes.Date) {
    return formatDateMMDDYYYY(date);
  } else {
    return formatDateYYYY(date);
  }
}

function priorYearCurrentPeriodToPeriodRange(date: Date, formatter: (date: Date) => string) {
  const startDate = new Date(Date.UTC(date.getUTCFullYear() - 1, date.getUTCMonth(), 1));
  const endDate = new Date(Date.UTC(date.getUTCFullYear() - 1, date.getUTCMonth() + 1, 0));
  return [formatter(startDate), formatter(endDate)];
}

function priorYearPriorPeriodToPeriodRange(date: Date, formatter: (date: Date) => string) {
  const startDate = new Date(Date.UTC(date.getUTCFullYear() - 1, date.getUTCMonth() - 1, 1));
  const endDate = new Date(Date.UTC(date.getUTCFullYear() - 1, date.getUTCMonth(), 0));
  return [formatter(startDate), formatter(endDate)];
}

function currentMonthDateRange(date) {
  const startDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  const endDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
  return [formatDate(startDate, BasicColumnTypes.Date), formatDate(endDate, BasicColumnTypes.Date)];
}

function priorMonthRange(date) {
  const startDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - 1, 1));
  const endDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 0));
  return [formatDate(startDate, BasicColumnTypes.Date), formatDate(endDate, BasicColumnTypes.Date)];
}

function currentFiscalQuarterRange(date, startMonth) {
  const fiscalYearStartMonth = startMonth - 1;
  const quarter = Math.floor((date.getUTCMonth() - fiscalYearStartMonth) / 3) + 1;
  const quarterStartMonth = (quarter - 1) * 3 + fiscalYearStartMonth;
  const quarterStartDate = new Date(Date.UTC(date.getUTCFullYear(), quarterStartMonth, 1));
  const quarterEndDate = new Date(Date.UTC(date.getUTCFullYear(), quarterStartMonth + 3, 0));
  return [quarterStartDate, quarterEndDate];
}

function currentCalendarQuarterDateRange(date) {
  const currentYear = date.getUTCFullYear();
  const currentQuarter = Math.floor((date.getUTCMonth() + 3) / 3);
  const quarterStartMonth = (currentQuarter - 1) * 3;
  const quarterStartDate = new Date(Date.UTC(currentYear, quarterStartMonth, 1));
  const quarterEndDate = new Date(Date.UTC(currentYear, quarterStartMonth + 3, 0));
  const endOfMonth = quarterEndDate.getUTCMonth();
  if (endOfMonth !== quarterStartMonth + 2) {
    quarterEndDate.setUTCMonth(endOfMonth + 1, 0);
  }
  return [quarterStartDate, quarterEndDate];
}

function rollingNCalendarQuarters({ date, rollingNumRange = 0, formatter }: {
  date: Date,
  rollingNumRange: number,
  formatter(date: Date): string
}) {
  const [currentCalendarQuarterStartDate, currentCalendarQuarterEndDate] = currentCalendarQuarterDateRange(date);
  const newStartDate = currentCalendarQuarterStartDate;
  if (rollingNumRange) {
    newStartDate.setUTCMonth(newStartDate.getUTCMonth() - 3 * (rollingNumRange - 1));
  }
  return [
    formatter(newStartDate),
    formatter(currentCalendarQuarterEndDate)
  ];
}

function rollingNMonthsDateRange({ date, rollingNumRange = 0, formatter }: {
  date: Date,
  rollingNumRange: number,
  formatter(date: Date): string
}) {
  let startDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  if (rollingNumRange) {
    startDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - rollingNumRange + 1, 1));
  }
  const endDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
  return [formatter(startDate), formatter(endDate)];
}

function rollingNFiscalQuarters({ date, startMonth, rollingNumRange = 0, formatter }: {
  date: Date,
  startMonth: number,
  rollingNumRange: number,
  formatter(date: Date): string
}) {
  const [currentFiscalQuarterStartDate, currentFiscalQuarterEndDate] = currentFiscalQuarterRange(date, startMonth);
  const newStartDate = new Date(
    Date.UTC(currentFiscalQuarterStartDate.getUTCFullYear(), currentFiscalQuarterStartDate.getUTCMonth(), 1)
  );
  if (rollingNumRange) {
    newStartDate.setUTCMonth(newStartDate.getUTCMonth() - 3 * (rollingNumRange - 1));
  }
  return [formatter(newStartDate), formatter(currentFiscalQuarterEndDate)];
}

function getCalendarYearRange(date: Date, range: YearRange): [Date, Date] {
  const currentYear = date.getUTCFullYear();
  const startYear = currentYear + range.from;
  const startDate = new Date(Date.UTC(startYear, 0, 1));

  let endDate: Date;
  if (range.to === 'date') {
    endDate = date;
  } else {
    const endYear = currentYear + range.to;
    endDate = new Date(Date.UTC(endYear, 11, 31));
  }

  return [startDate, endDate];
}

type YearRange = {
  from: number;
  to: number | 'date';
};

function getMonthRange(date: Date, range: YearRange, formatter = formatDateMMDDYYYY) {
  const startDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + range.from, 1));
  const endOffset = range.to === 'date' ? 0 : range.to;
  const endDate =
    range.to === 'date' ? date : new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + endOffset + 1, 0));
  return [formatter(startDate), formatter(endDate)];
}

export function getCalendarQuarter(date: Date) {
  return Math.floor(1 + date.getUTCMonth() / 3);
}

export function getCalendarQuarterRange(date: Date, range: YearRange, formatter = formatDateMMDDYYYY) {
  const currentQuarter = getCalendarQuarter(date);
  const currentYear = date.getUTCFullYear();

  // Calculate the start quarter and year
  const startQuarterOffset = range.from;
  const startQuarter = ((currentQuarter - 1 + startQuarterOffset + 4) % 4) + 1;
  const startYear = currentYear + Math.floor((currentQuarter - 1 + startQuarterOffset) / 4);

  // Calculate start date (first day of start quarter)
  const startMonth = (startQuarter - 1) * 3;
  const startDate = new Date(Date.UTC(startYear, startMonth, 1));

  let endDate: Date;
  if (range.to === 'date') {
    endDate = date;
  } else {
    const endQuarterOffset = range.to;
    const endQuarter = ((currentQuarter - 1 + endQuarterOffset + 4) % 4) + 1;
    const endYear = currentYear + Math.floor((currentQuarter - 1 + endQuarterOffset) / 4);

    // Calculate end date (last day of end quarter)
    const endMonth = (endQuarter - 1) * 3;
    endDate = new Date(Date.UTC(endYear, endMonth + 3, 0));
  }

  return [formatter(startDate), formatter(endDate)];
}

function getFiscalQuarterRange(
  date: Date,
  fiscalYearStartMonth: number,
  range: YearRange,
  formatter: (date: Date) => string = formatDateMMDDYYYY
) {
  const { from, to } = range;

  const getFiscalQuarter = (date, fiscalYearStartMonth) => {
    const month = date.getUTCMonth();
    const year = date.getUTCFullYear();
    const startMonth = fiscalYearStartMonth - 1;
    const fiscalMonth = (month - startMonth + 12) % 12;
    const quarter = Math.floor(fiscalMonth / 3) + 1;
    let fiscalYear = year;
    if (month < startMonth) {
      fiscalYear--;
    }
    return { quarter, fiscalYear };
  };

  const getQuarterStartDate = (quarter, fiscalYear, fiscalYearStartMonth) => {
    const startMonth = fiscalYearStartMonth - 1;
    const month = startMonth + (quarter - 1) * 3;
    const year = fiscalYear + Math.floor(month / 12);
    const normalizedMonth = month % 12;
    return new Date(Date.UTC(year, normalizedMonth, 1));
  };

  const getQuarterEndDate = (quarter, fiscalYear, fiscalYearStartMonth) => {
    const startDate = getQuarterStartDate(quarter, fiscalYear, fiscalYearStartMonth);
    const year = startDate.getUTCFullYear();
    const month = startDate.getUTCMonth();
    const endOfMonth = new Date(Date.UTC(year, month + 3, 0));
    return endOfMonth;
  };

  const { quarter: currentQuarter, fiscalYear: currentFiscalYear } = getFiscalQuarter(date, fiscalYearStartMonth);

  const fromQuarter = currentQuarter + from;
  const fromFiscalYear = currentFiscalYear + Math.floor((fromQuarter - 1) / 4);
  const normalizedFromQuarter = ((((fromQuarter - 1) % 4) + 4) % 4) + 1;
  const startDate = getQuarterStartDate(normalizedFromQuarter, fromFiscalYear, fiscalYearStartMonth);

  let endDate;
  if (to === 'date') {
    endDate = date;
  } else {
    const toQuarter = currentQuarter + to;
    const toFiscalYear = currentFiscalYear + Math.floor((toQuarter - 1) / 4);
    const normalizedToQuarter = ((((toQuarter - 1) % 4) + 4) % 4) + 1;
    endDate = getQuarterEndDate(normalizedToQuarter, toFiscalYear, fiscalYearStartMonth);
  }

  return [formatter(startDate), formatter(endDate)];
}

// Given a date and a fiscal year start month, return the fiscal year
function getFiscalYear(date: Date, startMonth: number): number {
  const year = date.getUTCFullYear();

  if (startMonth === 1) {
    return year;
  }

  const month = date.getUTCMonth() + 1;
  if (month < startMonth) {
    return year;
  }

  return year + 1;
}

// Given a year, just return the fiscal year that starts in that year.
function getFiscalYearStartDate(year, startMonth): Date {
  return new Date(Date.UTC(year, startMonth - 1, 1));
}

// Given a year, just return the ending fiscal year for the fiscal year that starts in that year.
function getFiscalYearEndDate(year, startMonth): Date {
  const startDate = getFiscalYearStartDate(year, startMonth);
  const endDate = new Date(startDate.getUTCFullYear() + 1, startDate.getUTCMonth(), 0);
  return endDate;
}

// Given a date and a fiscal year start month, return the fiscal year range
function getFiscalYearRange(date: Date, fiscalYearStartMonth: number, range: YearRange): [Date, Date] {
  const { from, to } = range;

  const currentMonth = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear() - (currentMonth < fiscalYearStartMonth ? 1 : 0);

  const startDate = getFiscalYearStartDate(year + from, fiscalYearStartMonth);
  const endDate = to === 'date' ? date : getFiscalYearEndDate(year + to, fiscalYearStartMonth);

  return [startDate, endDate];
}

function formatAsFiscalYear(date: Date, fiscalYearStartMonth: number) {
  return getFiscalYear(date, fiscalYearStartMonth).toString();
}

function getFiscalYearRangeFormatted(
  date: Date,
  fiscalYearStartMonth: number,
  range: YearRange,
  formatter: (date: Date) => string = (date: Date) => date.toISOString()
): [string, string] {
  const [startDate, endDate] = getFiscalYearRange(date, fiscalYearStartMonth, range);
  return [formatter(startDate), formatter(endDate)];
}

function getFiscalPriorYearRange(date, startMonth, type, offset = 0) {
  const lastYear = date.getUTCMonth() < startMonth - 1 ? date.getUTCFullYear() - 2 : date.getUTCFullYear() - 1;
  const lastFiscalYearStart = new Date(Date.UTC(lastYear - offset, startMonth - 1, 1));
  const lastFiscalYearEnd = new Date(Date.UTC(lastFiscalYearStart.getUTCFullYear() + offset + 1, startMonth - 1, 0));
  return [formatDate(lastFiscalYearStart, type), formatDate(lastFiscalYearEnd, type)];
}

function getFiscalCurrentAndPriorYearRange(date, startMonth, offset = 0) {
  const actualYear = date.getUTCFullYear();
  const fiscalYear = date.getUTCMonth() >= startMonth - 1 ? actualYear : actualYear - 1;
  const asOfDateStart = new Date(Date.UTC(fiscalYear - offset, startMonth - 1, 1));
  const asOfDateEnd = new Date(Date.UTC(fiscalYear + 1, startMonth - 1, 0));
  return [formatDateMMDDYYYY(asOfDateStart), formatDateMMDDYYYY(asOfDateEnd)];
}

function getFiscalYearToMonthRange(
  date: Date,
  startMonth: number,
  monthOffset: number,
  { formatter }: { formatter: (date: Date) => string }
) {
  const [startDate, _endDate] = getFiscalYearRange(date, startMonth, { from: 0, to: 0 });

  let endDate: Date;

  const isStartOfFiscalYear = date.getUTCMonth() === startMonth - 1;

  if (isStartOfFiscalYear && monthOffset < 0) {
    endDate = new Date(Date.UTC(date.getUTCFullYear(), startDate.getUTCMonth() + 1, 0));
  } else {
    const targetMonth = date.getUTCMonth() + monthOffset;
    endDate = new Date(Date.UTC(date.getUTCFullYear(), targetMonth + 1, 0));
  }

  return [formatter(startDate), formatter(endDate)];
}

function calculateFixedDateRangeForYearColumn(date: Date, startMonth: number, relativeFilterType: RelativeFilter) {
  switch (relativeFilterType) {
    case RelativeFilterOptions.InceptionToYear: {
      return [null, formatDate(date, BasicColumnTypes.Year)];
    }

    case RelativeFilterOptions.CurrentFiscalYear: {
      const [_startDate, endDate] = getFiscalYearRange(date, startMonth, { from: 0, to: 0 });
      const formattedYear = formatAsFiscalYear(endDate, startMonth);
      return [formattedYear, ''];
    }

    case RelativeFilterOptions.CurrentAndPriorFiscalYear: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: -1, to: 0 });
      return [formatAsFiscalYear(startDate, startMonth), formatAsFiscalYear(endDate, startMonth)];
    }

    case RelativeFilterOptions.PriorFiscalYear: {
      const [startDate] = getFiscalYearRange(date, startMonth, { from: -1, to: -1 });
      return [formatAsFiscalYear(startDate, startMonth), ''];
    }

    case RelativeFilterOptions.NextFiscalYear: {
      const [startDate] = getFiscalYearRange(date, startMonth, { from: 1, to: 1 });
      return [formatAsFiscalYear(startDate, startMonth), ''];
    }

    case RelativeFilterOptions.PriorTwoFiscalYears: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: -2, to: -1 });
      return [formatAsFiscalYear(startDate, startMonth), formatAsFiscalYear(endDate, startMonth)];
    }

    case RelativeFilterOptions.NextTwoFiscalYear: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: 1, to: 2 });
      return [formatAsFiscalYear(startDate, startMonth), formatAsFiscalYear(endDate, startMonth)];
    }

    case RelativeFilterOptions.CurrentAndPriorTwoFiscalYears: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: -2, to: 0 });
      return [formatAsFiscalYear(startDate, startMonth), formatAsFiscalYear(endDate, startMonth)];
    }

    case RelativeFilterOptions.PriorCalendarYear: {
      const [startDate] = getCalendarYearRange(date, { from: -1, to: -1 });
      return [formatDateYYYY(startDate), ''];
    }

    case RelativeFilterOptions.CurrentCalendarYear: {
      const [startDate] = getCalendarYearRange(date, { from: 0, to: 0 });
      return [formatDateYYYY(startDate), ''];
    }

    case RelativeFilterOptions.NextCalendarYear: {
      const [startDate] = getCalendarYearRange(date, { from: 1, to: 1 });
      return [formatDateYYYY(startDate), ''];
    }

    default: {
      return [];
    }
  }
}

function calculateFixedDateRangeForDateColumn(date, startMonth, relativeFilterType) {
  switch (relativeFilterType) {
    case RelativeFilterOptions.InceptionToDate: {
      return [null, formatDate(date, BasicColumnTypes.Date)];
    }

    case RelativeFilterOptions.CurrentFiscalYear: {
      return getFiscalYearRangeFormatted(date, startMonth, { from: 0, to: 0 }, formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.CurrentAndPriorFiscalYear: {
      return getFiscalCurrentAndPriorYearRange(date, startMonth, 1);
    }

    case RelativeFilterOptions.PriorFiscalYear: {
      return getFiscalPriorYearRange(date, startMonth, BasicColumnTypes.Date);
    }

    case RelativeFilterOptions.NextFiscalYear: {
      return getFiscalYearRangeFormatted(date, startMonth, { from: 1, to: 1 }, formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.PriorTwoFiscalYears: {
      return getFiscalPriorYearRange(date, startMonth, BasicColumnTypes.Date, 1);
    }

    case RelativeFilterOptions.NextTwoFiscalYear: {
      return getFiscalYearRangeFormatted(date, startMonth, { from: 1, to: 2 }, formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.CurrentAndPriorTwoFiscalYears: {
      return getFiscalYearRangeFormatted(date, startMonth, { from: -2, to: 0 }, formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.CurrentFiscalYearToDate: {
      return getFiscalYearRangeFormatted(date, startMonth, { from: 0, to: 'date' }, formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.CurrentFiscalYearToCurrentMonth: {
      return getFiscalYearToMonthRange(date, startMonth, 0, { formatter: formatDateMMDDYYYY });
    }

    case RelativeFilterOptions.CurrentFiscalYearToPriorMonth: {
      return getFiscalYearToMonthRange(date, startMonth, -1, { formatter: formatDateMMDDYYYY });
    }

    case RelativeFilterOptions.PriorFiscalYearToDate: {
      return getFiscalYearRangeFormatted(date, startMonth, { from: -1, to: 'date' }, formatDateMMDDYYYY);
    }

    //Calendar years
    case RelativeFilterOptions.CurrentCalendarYear: {
      const [startDate, endDate] = getCalendarYearRange(date, { from: 0, to: 0 });
      return [formatDate(startDate, BasicColumnTypes.Date), formatDate(endDate, BasicColumnTypes.Date)];
    }

    case RelativeFilterOptions.PriorCalendarYear: {
      const [startDate, endDate] = getCalendarYearRange(date, { from: -1, to: -1 });
      return [formatDate(startDate, BasicColumnTypes.Date), formatDate(endDate, BasicColumnTypes.Date)];
    }

    case RelativeFilterOptions.NextCalendarYear: {
      return getCalendarYearRange(date, { from: 1, to: 1 }).map(formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.CurrentCalendarYearToDate: {
      return getCalendarYearRange(date, { from: 0, to: 'date' }).map(formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.PriorCalendarYearToDate: {
      return getCalendarYearRange(date, { from: -1, to: 'date' }).map(formatDateMMDDYYYY);
    }

    //Fiscal Quarters
    case RelativeFilterOptions.CurrentFiscalQuarter: {
      return getFiscalQuarterRange(date, startMonth, { from: 0, to: 0 }, formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.CurrentFiscalQuarterToDate: {
      return getFiscalQuarterRange(date, startMonth, { from: 0, to: 'date' }, formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.PriorFiscalQuarter: {
      return getFiscalQuarterRange(date, startMonth, { from: -1, to: -1 }, formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.PriorFiscalQuarterToDate: {
      return getFiscalQuarterRange(date, startMonth, { from: -1, to: 'date' }, formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.NextFiscalQuarter: {
      return getFiscalQuarterRange(date, startMonth, { from: 1, to: 1 }, formatDateMMDDYYYY);
    }

    // Calendar Quarters
    case RelativeFilterOptions.CurrentCalendarQuarter: {
      return getCalendarQuarterRange(date, { from: 0, to: 0 });
    }

    case RelativeFilterOptions.CurrentCalendarQuarterToDate: {
      return getCalendarQuarterRange(date, { from: 0, to: 'date' });
    }

    case RelativeFilterOptions.PriorCalendarQuarter: {
      return getCalendarQuarterRange(date, { from: -1, to: -1 });
    }

    case RelativeFilterOptions.PriorCalendarQuarterToDate: {
      return getCalendarQuarterRange(date, { from: -1, to: 'date' });
    }

    case RelativeFilterOptions.NextCalendarQuarter: {
      return getCalendarQuarterRange(date, { from: 1, to: 1 });
    }

    // Months
    case RelativeFilterOptions.CurrentMonth: {
      return currentMonthDateRange(date);
    }

    case RelativeFilterOptions.CurrentMonthToDate: {
      const [currentMonthStart] = currentMonthDateRange(date);
      return [currentMonthStart, formatDate(date, BasicColumnTypes.Date)];
    }

    case RelativeFilterOptions.PriorMonth: {
      return priorMonthRange(date);
    }

    case RelativeFilterOptions.PriorMonthToDate: {
      return getMonthRange(date, { from: -1, to: 'date' }, formatDateMMDDYYYY);
    }

    case RelativeFilterOptions.NextMonth: {
      return getMonthRange(date, { from: 1, to: 1 }, formatDateMMDDYYYY);
    }

    default:
      return [];
  }
}

function monthAsFiscalPeriod(month: number, startMonth: number): number {
  return ((month - startMonth + 12) % 12) + 1;
}

function dateToPeriodString(date: Date, startMonth: number): string {
  const fiscalYear = getFiscalYear(date, startMonth);
  const fiscalPeriod = monthAsFiscalPeriod(date.getUTCMonth() + 1, startMonth);

  return `${fiscalYear}-P${fiscalPeriod.toString().padStart(3, '0')}`;
}

function calculateFixedDateRangeForPeriodColumn(date: Date, startMonth: number, relativeFilterType: RelativeFilter) {
  const formatter = (date: Date) => dateToPeriodString(date, startMonth);
  switch (relativeFilterType) {
    case RelativeFilterOptions.InceptionToPeriod: {
      return [null, formatter(date)];
    }

    // Fiscal Years
    case RelativeFilterOptions.CurrentFiscalYear: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: 0, to: 0 });
      return [formatter(startDate), formatter(endDate)];
    }
    case RelativeFilterOptions.CurrentFiscalYearToCurrentPeriod: {
      return getFiscalYearToMonthRange(date, startMonth, 0, { formatter });
    }
    case RelativeFilterOptions.CurrentFiscalYearToPriorPeriod: {
      return getFiscalYearToMonthRange(date, startMonth, -1, { formatter });
    }
    case RelativeFilterOptions.CurrentAndPriorFiscalYear: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: -1, to: 0 });
      return [formatter(startDate), formatter(endDate)];
    }
    case RelativeFilterOptions.CurrentAndPriorTwoFiscalYears: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: -2, to: 0 });
      return [formatter(startDate), formatter(endDate)];
    }
    case RelativeFilterOptions.PriorFiscalYear: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: -1, to: -1 });
      return [formatter(startDate), formatter(endDate)];
    }
    case RelativeFilterOptions.PriorFiscalYearToCurrentPeriod: {
      const [startDate] = getFiscalYearRange(date, startMonth, { from: -1, to: 0 });
      return [formatter(startDate), formatter(date)];
    }
    case RelativeFilterOptions.PriorTwoFiscalYears: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: -2, to: -1 });
      return [formatter(startDate), formatter(endDate)];
    }
    case RelativeFilterOptions.NextFiscalYear: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: 1, to: 1 });
      return [formatter(startDate), formatter(endDate)];
    }
    case RelativeFilterOptions.NextTwoFiscalYear: {
      const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: 1, to: 2 });
      return [formatter(startDate), formatter(endDate)];
    }

    // Fiscal Quarters
    case RelativeFilterOptions.CurrentFiscalQuarter: {
      return getFiscalQuarterRange(date, startMonth, { from: 0, to: 0 }, formatter);
    }
    case RelativeFilterOptions.CurrentFiscalQuarterToCurrentPeriod: {
      return getFiscalQuarterRange(date, startMonth, { from: 0, to: 'date' }, formatter);
    }
    case RelativeFilterOptions.PriorFiscalQuarter: {
      return getFiscalQuarterRange(date, startMonth, { from: -1, to: -1 }, formatter);
    }
    case RelativeFilterOptions.PriorFiscalQuarterToCurrentPeriod: {
      return getFiscalQuarterRange(date, startMonth, { from: -1, to: 'date' }, formatter);
    }
    case RelativeFilterOptions.NextFiscalQuarter: {
      return getFiscalQuarterRange(date, startMonth, { from: 1, to: 1 }, formatter);
    }

    // Calendar years
    case RelativeFilterOptions.CurrentCalendarYear: {
      const [startDate, endDate] = getCalendarYearRange(date, { from: 0, to: 0 });
      return [formatter(startDate), formatter(endDate)];
    }
    case RelativeFilterOptions.CurrentCalendarYearToCurrentPeriod: {
      const [startDate, endDate] = getCalendarYearRange(date, { from: 0, to: 'date' });
      return [formatter(startDate), formatter(endDate)];
    }
    case RelativeFilterOptions.PriorCalendarYear: {
      const [startDate, endDate] = getCalendarYearRange(date, { from: -1, to: -1 });
      return [formatter(startDate), formatter(endDate)];
    }
    case RelativeFilterOptions.PriorCalendarYearToCurrentPeriod: {
      const [startPeriod] = getCalendarYearRange(date, { from: -1, to: 'date' });
      return [formatter(startPeriod), formatter(date)];
    }
    case RelativeFilterOptions.NextCalendarYear: {
      const [startDate, endDate] = getCalendarYearRange(date, { from: 1, to: 1 });
      return [formatter(startDate), formatter(endDate)];
    }

    // Calendar Quarters
    case RelativeFilterOptions.CurrentCalendarQuarter: {
      return getCalendarQuarterRange(date, { from: 0, to: 0 }, formatter);
    }
    case RelativeFilterOptions.CurrentCalendarQuarterToCurrentPeriod: {
      return getCalendarQuarterRange(date, { from: 0, to: 'date' }, formatter);
    }
    case RelativeFilterOptions.PriorCalendarQuarter: {
      return getCalendarQuarterRange(date, { from: -1, to: -1 }, formatter);
    }
    case RelativeFilterOptions.PriorCalendarQuarterToCurrentPeriod: {
      return getCalendarQuarterRange(date, { from: -1, to: 'date' }, formatter);
    }
    case RelativeFilterOptions.NextCalendarQuarter: {
      return getCalendarQuarterRange(date, { from: 1, to: 1 }, formatter);
    }
    
    // Months
    case RelativeFilterOptions.CurrentMonth: {
      return getMonthRange(date, { from: 0, to: 0 }, formatter);
    }
    case RelativeFilterOptions.InceptionToCurrentMonth: {
      return [null, formatter(date)];
    }
    case RelativeFilterOptions.PriorMonth: {
      return getMonthRange(date, { from: -1, to: -1 }, formatter);
    }
    case RelativeFilterOptions.NextMonth: {
      return getMonthRange(date, { from: 1, to: 1 }, formatter);
    }

    // Periods
    case RelativeFilterOptions.CurrentPeriod: {
      return getMonthRange(date, { from: 0, to: 0 }, formatter);
    }
    case RelativeFilterOptions.PriorPeriod: {
      return getMonthRange(date, { from: -1, to: -1 }, formatter);
    }
    case RelativeFilterOptions.PriorYearCurrentPeriod: {
      return priorYearCurrentPeriodToPeriodRange(date, formatter);
    }
    case RelativeFilterOptions.PriorYearPriorPeriod: {
      return priorYearPriorPeriodToPeriodRange(date, formatter);
    }

    default:
      return [];
  }
}

export const calculateRollingNDateRange = (
  date: Date,
  startMonth: number,
  relativeFilterType: RelativeFilter,
  basicColumnType: BasicColumnTypes,
  rolling: number
) => {
  if (basicColumnType === BasicColumnTypes.Date) {
    const formatter = (date: Date) => formatDate(date, BasicColumnTypes.Date);

    switch (relativeFilterType) {
      case RelativeFilterOptions.RollingNFiscalYears: {
        const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: -rolling + 1, to: 0 });
        return [formatter(startDate), formatter(endDate)];
      }
      case RelativeFilterOptions.RollingNCalenderYears: {
        const [startDate, endDate] = getCalendarYearRange(date, { from: -rolling + 1, to: 0 });
        return [formatter(startDate), formatter(endDate)];
      }

      case RelativeFilterOptions.RollingNFiscalQuarters: {
        return rollingNFiscalQuarters({ date, startMonth, rollingNumRange: rolling, formatter });
      }

      case RelativeFilterOptions.RollingNCalendarQuarters: {
        return rollingNCalendarQuarters({ date, rollingNumRange: rolling, formatter });
      }

      case RelativeFilterOptions.RollingNMonths: {
        return rollingNMonthsDateRange({ date, rollingNumRange: rolling, formatter });
      }
      default:
        break;
    }
  } else if (basicColumnType === BasicColumnTypes.Year) {
    switch (relativeFilterType) {
      case RelativeFilterOptions.RollingNFiscalYears: {
        const from = -rolling + 1;
        const to = 0;
        const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from, to });
        return rolling === 1
          ? [formatAsFiscalYear(startDate, startMonth), '']
          : [formatAsFiscalYear(startDate, startMonth), formatAsFiscalYear(endDate, startMonth)];
      }

      case RelativeFilterOptions.RollingNCalenderYears: {
        const from = -rolling + 1;
        const to = 0;
        const [startDate, endDate] = getCalendarYearRange(date, { from, to });
        return rolling === 1 ? [formatDateYYYY(startDate), ''] : [formatDateYYYY(startDate), formatDateYYYY(endDate)];
      }

      default:
        break;
    }
  } else if (basicColumnType === BasicColumnTypes.Period) {
    const formatter = (date: Date) => dateToPeriodString(date, startMonth);

    switch (relativeFilterType) {
      case RelativeFilterOptions.RollingNFiscalYears: {
        const [startDate, endDate] = getFiscalYearRange(date, startMonth, { from: -rolling + 1, to: 0 });
        return [formatter(startDate), formatter(endDate)];
      }
      case RelativeFilterOptions.RollingNFiscalQuarters: {
        return rollingNFiscalQuarters({ date, startMonth, rollingNumRange: rolling, formatter });
      }
      case RelativeFilterOptions.RollingNCalenderYears: {
        const [startDate, endDate] = getCalendarYearRange(date, { from: -rolling + 1, to: 0 });
        return [formatter(startDate), formatter(endDate)];
      }
      case RelativeFilterOptions.RollingNCalendarQuarters: {
        return rollingNCalendarQuarters({ date, rollingNumRange: rolling, formatter });
      }
      case RelativeFilterOptions.RollingNMonths: {
        return rollingNMonthsDateRange({ date, rollingNumRange: rolling, formatter });
      }
      case RelativeFilterOptions.RollingNPeriods: {
        return rollingNMonthsDateRange({ date, rollingNumRange: rolling, formatter });
      }
      default:
        break;
    }
  }

  return [];
};

export const calculateFixedDateRange = (
  date: Date,
  startMonth: number,
  relativeFilterType: RelativeFilter,
  columnType: BasicColumnTypes
) => {
  if (columnType === BasicColumnTypes.Date) {
    return calculateFixedDateRangeForDateColumn(date, startMonth, relativeFilterType);
  } else if (columnType === BasicColumnTypes.Year) {
    return calculateFixedDateRangeForYearColumn(date, startMonth, relativeFilterType);
  } else if (columnType === BasicColumnTypes.Period) {
    return calculateFixedDateRangeForPeriodColumn(date, startMonth, relativeFilterType);
  } else {
    return [];
  }
};

function convertAsOfToUTCDate(
  asOfDateString: string,
  columnType: BasicColumnTypes,
  entityTimezone?: string | null
) {
  let asOfDate = asOfDateString;
  if (asOfDate.length === 0) {
    if (columnType === BasicColumnTypes.Period) {
      // if asOfDate empty and column type is period, we need to get the last day of the current
      // month in the entity timezone (falls back to UTC when timezone is missing/invalid)
      asOfDate = endOfMonthInTimezone(entityTimezone);
    } else {
      asOfDate = todayInTimezone(entityTimezone);
    }
  }
  const [year, month, day] = asOfDate.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function normalizeAsOfDate(asOfDateString: string) {
  // If we detect a slash, we assume asOfDateString is in MM/DD/YYYY format,
  // since that's how it's formatted in the UI and saved in the report configuration.
  if (asOfDateString.includes('/')) {
    const parts = asOfDateString.split('/');
    const [month, day, year] = parts;
    return `${year}-${month}-${day}`;
  }
  // Assume it's in YYYY-MM-DD format
  return asOfDateString;
}

export function getRelativeDateRange(
  relativeRange: RelativeFilter,
  rolling: number,
  asOfDateString: string,
  fiscalYearStartMonth: number,
  columnType: BasicColumnTypes,
  entityTimezone?: string | null
) {
  const asOfDate = convertAsOfToUTCDate(normalizeAsOfDate(asOfDateString), columnType, entityTimezone);

  if (!dayjs(asOfDate).isValid()) {
    throw new Error(`Invalid date for relative date range: ${asOfDateString}`);
  }

  if (rollingNFilterOptions.includes(relativeRange)) {
    return calculateRollingNDateRange(asOfDate, fiscalYearStartMonth, relativeRange, columnType, rolling);
  } else if (fixedRelativeFilterOptions.includes(relativeRange)) {
    return calculateFixedDateRange(asOfDate, fiscalYearStartMonth, relativeRange, columnType);
  } else {
    return [];
  }
}
